import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { deleteFCMToken } from '../firebase'
import { resolveApiBaseUrl } from './api-base-url'

const API_BASE_URL = resolveApiBaseUrl()

// Default timeout for most API calls (30 seconds)
export const DEFAULT_TIMEOUT = 30000

// Extended timeout for AI-intensive operations that take longer (60 seconds)
export const EXTENDED_TIMEOUT = 60000

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: DEFAULT_TIMEOUT,
})

// Request interceptor - Add auth token to requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    config.baseURL = resolveApiBaseUrl()

    // Get token from localStorage
    let token = null
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('auth_token')
    }

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error: AxiosError) => {
    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('auth_user')

        // Clear FCM token (fire-and-forget; never block the 401 path)
        void deleteFCMToken()

        const path = window.location.pathname
        const onAuthPage =
          path.startsWith('/login') ||
          path.startsWith('/signup') ||
          path.startsWith('/register') ||
          path.startsWith('/forgot-password') ||
          path.startsWith('/reset-password') ||
          path.startsWith('/email-verification')

        // Stay on auth pages — do not hard-reload into a redirect loop
        if (!onAuthPage) {
          window.location.href = '/login'
        }
      }
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      const data = error.response.data as any;
      if (data?.error_code === 9001) {
        console.error('Subscription limit exceeded (9001):', data.message?.description || 'Upgrade required');
        // Dispatch custom event to trigger upgrade modal
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('subscription_limit_exceeded', { detail: data }));
        }
      } else {
        console.error('Access forbidden (403)');
      }
    }

    // Handle 500 Server Error
    if (error.response?.status === 500) {
      console.error('Server error occurred')
    }

    // Handle 422 Unprocessable Entity
    if (error.response?.status === 422) {
      console.error('Validation error (422):', error.response.data);
    }

    return Promise.reject(error)
  }
)

export default apiClient
